import React, { useEffect, useMemo, useState } from "react";
import { api, rupiah, downloadReport, formatApiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/common";
import { usePaged, Pager } from "@/components/Pager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Search, ShoppingBag, Loader2, FileText, FileSpreadsheet, Printer, CheckCircle2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["", "Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const nowPeriod = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const pLabel = (p) => { const [y, m] = (p || "").split("-"); return m ? `${MONTHS[Number(m)]} ${y}` : p; };

export default function LaporanBelanjaPendidik() {
  const [rows, setRows] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState(nowPeriod());
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState("");

  const loadPeriods = () => api.get("/admin/educator-periods").then((r) => {
    const list = r.data.includes(period) ? r.data : [period, ...r.data];
    setPeriods(list);
  }).catch(() => {});

  const load = () => {
    setLoading(true);
    api.get("/admin/educator-spending", { params: { period } })
      .then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { loadPeriods(); }, []);
  useEffect(() => { load(); }, [period]);

  const withSpending = useMemo(() => rows.filter((r) => r.total_spending > 0), [rows]);
  const filtered = withSpending.filter((r) =>
    r.name.toLowerCase().includes(query.toLowerCase()) || (r.nik || "").includes(query));
  const pg = usePaged(filtered, 10);
  const grand = withSpending.reduce((a, r) => a + r.total_spending, 0);
  const hasData = withSpending.length > 0;

  const toggleSettle = async (r) => {
    try {
      if (r.settled) {
        await api.delete("/admin/educator-settlements", { params: { educator_id: r.id, period } });
        toast.success(`Status lunas ${r.name} dibatalkan`);
      } else {
        await api.post("/admin/educator-settlements", { educator_id: r.id, period });
        toast.success(`${r.name} ditandai LUNAS untuk ${pLabel(period)}`);
      }
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const dl = async (kind, r) => {
    setBusy(kind + (r?.id || ""));
    try {
      if (kind === "slip-one") await downloadReport("/admin/educator-slip", { period, educator_id: r.id }, `slip_${r.name}_${period}.pdf`);
      else if (kind === "slip-all") await downloadReport("/admin/educator-slip", { period, educator_id: "all" }, `slip_gaji_${period}.pdf`);
      else if (kind === "rekap-pdf") await downloadReport("/admin/educator-rekap", { period, format: "pdf" }, `rekap_potong_gaji_${period}.pdf`);
      else if (kind === "rekap-xlsx") await downloadReport("/admin/educator-rekap", { period, format: "xlsx" }, `rekap_potong_gaji_${period}.xlsx`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Gagal mengunduh"); }
    finally { setBusy(""); }
  };

  return (
    <div>
      <PageHeader
        title="Laporan Belanja Pendidik"
        subtitle="Total belanja kopontren tiap pendidik & kependidikan per bulan. Cetak slip potong gaji dan tandai lunas."
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={!hasData || busy === "slip-all"} onClick={() => dl("slip-all")} data-testid="print-all-slip" className="rounded-xl">
              {busy === "slip-all" ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <Printer size={16} className="mr-1.5" />} Cetak Semua Slip
            </Button>
            <Button variant="outline" disabled={!hasData || busy === "rekap-pdf"} onClick={() => dl("rekap-pdf")} data-testid="rekap-pdf" className="rounded-xl">
              {busy === "rekap-pdf" ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <FileText size={16} className="mr-1.5" />} Rekap PDF
            </Button>
            <Button variant="outline" disabled={!hasData || busy === "rekap-xlsx"} onClick={() => dl("rekap-xlsx")} data-testid="rekap-xlsx" className="rounded-xl">
              {busy === "rekap-xlsx" ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <FileSpreadsheet size={16} className="mr-1.5" />} Rekap Excel
            </Button>
          </div>
        }
      />

      <div className="grid sm:grid-cols-3 gap-4 mb-5">
        <div className="bg-emerald-600 text-white rounded-2xl p-5 shadow-sm">
          <p className="text-emerald-100 text-sm">Total Belanja — {pLabel(period)}</p>
          <p className="font-heading text-3xl font-bold mt-1" data-testid="grand-total">{rupiah(grand)}</p>
        </div>
        <div>
          <label className="text-xs text-stone-500 mb-1 block">Periode Bulan</label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="rounded-xl bg-white h-12" data-testid="period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {periods.map((p) => <SelectItem key={p} value={p}>{pLabel(p)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="relative flex items-end">
          <Search size={18} className="absolute left-3 bottom-3.5 text-stone-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="spending-search" placeholder="Cari nama / NIK…" className="pl-10 rounded-xl bg-white h-12" />
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
        ) : filtered.length === 0 ? (
          <Empty><ShoppingBag className="mx-auto mb-2" /> Belum ada belanja pendidik pada {pLabel(period)}.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nama</TableHead><TableHead>Kategori</TableHead>
                <TableHead className="text-center">Jml Transaksi</TableHead>
                <TableHead className="text-right">Total Belanja</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pg.pageRows.map((r) => (
                <TableRow key={r.id} data-testid={`spending-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name}<span className="block text-xs text-stone-400">{r.jabatan || r.nik || "-"}</span></TableCell>
                  <TableCell><span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${r.category === "kependidikan" ? "bg-sky-50 text-sky-700" : "bg-emerald-50 text-emerald-700"}`}>{r.category === "kependidikan" ? "Kependidikan" : "Pendidik"}</span></TableCell>
                  <TableCell className="text-center">{r.tx_count}</TableCell>
                  <TableCell className="text-right font-heading font-bold text-emerald-700">
                    {rupiah(r.total_spending)}
                    {r.outstanding > 0 && r.outstanding < r.total_spending && (
                      <span className="block text-xs font-normal text-amber-600">Belum lunas: {rupiah(r.outstanding)}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {r.status === "lunas"
                      ? <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-emerald-100 text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={12} /> Lunas</span>
                      : r.status === "sebagian"
                      ? <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-sky-100 text-sky-700">Sebagian</span>
                      : <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-700">Belum Lunas</span>}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => dl("slip-one", r)} disabled={busy === "slip-one" + r.id} data-testid={`slip-${r.id}`} title="Cetak slip">
                      {busy === "slip-one" + r.id ? <Loader2 size={15} className="animate-spin" /> : <Printer size={15} />}
                    </Button>
                    <Button size="sm" variant={r.settled ? "ghost" : "outline"} onClick={() => toggleSettle(r)} data-testid={`settle-${r.id}`} className={r.settled ? "text-stone-500" : "text-emerald-700 border-emerald-200"}>
                      {r.settled ? <><RotateCcw size={14} className="mr-1" /> Batal</> : <><CheckCircle2 size={14} className="mr-1" /> Lunas</>}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Pager {...pg} />
      </div>
    </div>
  );
}
